import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchInvoiceEditData } from '../v2/features/customer/customer.slice';
import { getTimesheetWorkingHours } from '../v2/features/timesheet/timesheet.slice';
import '../v2/pages/timesheet/timeSheetsPage/timeSheetsPage.scss';

interface InvoiceByTimesheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InvoiceByTimesheetData) => void;
}

export interface InvoiceByTimesheetData {
  clientId: string;
  fromDate: string;
  toDate: string;
}

const InvoiceByTimesheetModal: React.FC<InvoiceByTimesheetModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get user ID from Redux auth state
  const userId = useSelector((state: any) => state.auth?.user?.userId);
  
  // Get invoice edit data (including customers) from Redux customer state
  const { invoiceEditData, loading } = useSelector((state: any) => state.customer);
  
  // Extract customers from invoiceEditData
  const customers = invoiceEditData?.response?.customers || [];
  
  const [clientId, setClientId] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [timesheetResponseData, setTimesheetResponseData] = useState<any>(null);
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const clientDropdownRef = React.useRef<HTMLDivElement>(null);
  
  // Validation error states
  const [clientError, setClientError] = useState<string>('');
  const [fromDateError, setFromDateError] = useState<string>('');
  const [toDateError, setToDateError] = useState<string>('');

  // Fetch customer data when component mounts or userId changes
  useEffect(() => {
    if (userId && !invoiceEditData) {
      dispatch(fetchInvoiceEditData(userId) as any);
    }
  }, [dispatch, userId, invoiceEditData]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setIsClientDropdownOpen(false);
      }
    };

    if (isClientDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isClientDropdownOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    // Clear previous errors
    setClientError('');
    setFromDateError('');
    setToDateError('');
    
    // Validate required fields
    let hasError = false;
    
    if (!clientId || clientId.trim() === '') {
      setClientError(t('CLIENT_REQUIRED') || 'Client is required');
      hasError = true;
    }
    
    if (!fromDate || fromDate.trim() === '') {
      setFromDateError(t('FROM_DATE_REQUIRED') || 'From date is required');
      hasError = true;
    }
    
    if (!toDate || toDate.trim() === '') {
      setToDateError(t('TO_DATE_REQUIRED') || 'To date is required');
      hasError = true;
    }
    
    // If any error exists, stop submission
    if (hasError) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the GET API to fetch timesheet data
      const result = await dispatch(getTimesheetWorkingHours({
        fromDate: fromDate || '',
        toDate: toDate || '',
        Month: '',
        year: '',
        customerId: clientId || ''
      }) as any).unwrap();

      // Check if timeSheetWorkingHoursV2List is empty
      const workingHoursList = result?.response?.timeSheetWorkingHoursV2List || [];
      
      if (!workingHoursList || workingHoursList.length === 0) {
        // Show error toast if no working hours found
        toast.error(String(t('NO_TIMESHEET_WORKING_HOURS_FOUND') || 'No timesheet working hours found. Please create and save timesheet working hours first, then create invoice.'));
        return; // Don't redirect or close modal
      }
      
      // Save response to state
      setTimesheetResponseData(result);

      // Call the parent onSubmit callback
      onSubmit({
        clientId,
        fromDate,
        toDate,
      });

      // Redirect based on current language
      const currentLanguage = i18n.language;
      const langParam = currentLanguage === 'sv' || currentLanguage === 'sv-SE' ? 'sv-se' : 'en-us';
      navigate(`/v2/create-invoice?lang=${langParam}&timesheet=true`);
      
      // Close modal after successful submission
      onClose();
    } catch (error) {
      console.error('Failed to fetch timesheet data:', error);
      // Keep modal open on error so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="invoice-timesheet-modal">
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title" style={{color: '#000',
fontFamily: "Plus Jakarta Sans",
fontSize: '16px',
fontStyle: 'normal',
fontWeight: '600',
lineHeight: 'normal',
letterSpacing: '-0.16px',}}>
            {t('CREATE_INVOICE_BY_TIMESHEET') || 'Create invoice by timesheet'}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <p className="modal-description" style={{color: '#000',
fontFamily: "Plus Jakarta Sans",
fontSize: '15px',
fontStyle: 'normal',
fontWeight: '400',
lineHeight: '22px', /* 146.667% */}}>
            {t('INVOICE_MODAL_DESCRIPTION') ||
              'Please enter the below details and it will update your invoice accordingly.'}
          </p>

          {/* Client Details Field */}
          <div className="modal-field">
            <label className="modal-field-label">
              {t('CLIENT_DETAILS') || 'Client Details'}
            </label>
            <div className="modal-input-wrapper custom-dropdown" ref={clientDropdownRef}>
              <div 
                className={`dropdown-trigger ${isClientDropdownOpen ? 'open' : ''} ${clientError ? 'error' : ''}`}
                onClick={() => setIsClientDropdownOpen(!isClientDropdownOpen)}
                style={{
                  border: clientError ? '1px solid #DC3545' : '',
                  boxShadow: clientError ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : ''
                }}
              >
                <span className="dropdown-value">
                  {clientId && customers.length > 0
                    ? customers.find((c: any) => String(c.customerId || c.id) === String(clientId))?.name || t('SELECT_CLIENT')
                    : t('SELECT_CLIENT')}
                </span>
                <svg className={`dropdown-arrow ${isClientDropdownOpen ? 'rotated' : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path 
                    d="M5 7.5L10 12.5L15 7.5" 
                    stroke="#68727D" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {isClientDropdownOpen && (
                <div className="dropdown-list">
                  <div className="dropdown-content">
                    {loading ? (
                      <div className="list-item no-results">
                        <div className="text-input">
                          <div className="paragraph-container">
                            <p className="option-text">{t('LOADING') || 'Loading...'}</p>
                          </div>
                        </div>
                      </div>
                    ) : customers.length > 0 ? (
                      customers.map((customer: any) => (
                        <div
                          key={customer.customerId || customer.id}
                          className={`list-item ${String(clientId) === String(customer.customerId || customer.id) ? 'selected' : ''}`}
                          onClick={() => {
                            setClientId(String(customer.customerId || customer.id));
                            setClientError(''); // Clear error when client selected
                            setIsClientDropdownOpen(false);
                          }}
                        >
                          <div className="text-input">
                            <div className="paragraph-container">
                              <p className="option-text">{customer.name}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="list-item no-results">
                        <div className="text-input">
                          <div className="paragraph-container">
                            <p className="option-text">{t('NO_CLIENTS_FOUND')}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {clientError && (
              <p style={{
                color: '#DC3545',
                fontSize: '12px',
                marginTop: '4px',
                marginBottom: '0'
              }}>
                {clientError}
              </p>
            )}
          </div>

         
          <div className="modal-field">
            <label className="modal-field-label">
              {t('FROM_DATE') || 'From Date'}
            </label>
            <div className="modal-input-wrapper">
              <input
                type="date"
                className="modal-input"
                placeholder="dd/mm/yyyy"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setFromDateError(''); // Clear error when date selected
                }}
                style={{
                  border: fromDateError ? '1px solid #DC3545' : '1px solid #EAEBF0',
                  boxShadow: fromDateError ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : 'none'
                }}
              />
            </div>
            {fromDateError && (
              <p style={{
                color: '#DC3545',
                fontSize: '12px',
                marginTop: '4px',
                marginBottom: '0'
              }}>
                {fromDateError}
              </p>
            )}
          </div>

          {/* To Date Field */}
          <div className="modal-field">
            <label className="modal-field-label">
              {t('TO_DATE') || 'To Date'}
            </label>
            <div className="modal-input-wrapper">
              <input
                type="date"
                className="modal-input"
                placeholder="dd/mm/yyyy"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setToDateError(''); // Clear error when date selected
                }}
                style={{
                  border: toDateError ? '1px solid #DC3545' : '1px solid #EAEBF0',
                  boxShadow: toDateError ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : 'none'
                }}
              />
            </div>
            {toDateError && (
              <p style={{
                color: '#DC3545',
                fontSize: '12px',
                marginTop: '4px',
                marginBottom: '0'
              }}>
                {toDateError}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer d-flex">
          <button className="modal-cancel-btn" onClick={onClose} disabled={isSubmitting}>
            {t('CANCEL') || 'Cancel'}
          </button>
          <button className="modal-submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <svg className="spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }}>
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="31.4 31.4" strokeDashoffset="0">
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 8 8"
                      to="360 8 8"
                      dur="1s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </svg>
                {t('SUBMITTING') || 'Submitting...'}
              </>
            ) : (
              t('SUBMIT') || 'Submit'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceByTimesheetModal;
